import { CourseEripBillMutation, CourseOfferQuery } from "@/__generated__/graphql";
import BankLinkView from "@/modules/balance/views/BankLinkView";
import { CheckCircle, ContentCopy, Done } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  Stack,
  Switch,
  Typography,
} from "@mui/joy";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { CASH_BRANCHES } from "../courses.config";

type Offer = CourseOfferQuery["courseOffer"];
type Bill = CourseEripBillMutation["courseEripBill"];

const money = (value: number, currency: string) => `${value.toFixed(2)} ${currency}`;

export default function CourseEnrollView({
  offer,
  useBonus,
  onToggleBonus,
  onPayOnline,
  requesting,
  paying,
  onBack,
  bill,
  onClose,
}: {
  offer: Offer;
  useBonus: boolean;
  onToggleBonus: (value: boolean) => void;
  onPayOnline: () => void;
  requesting: boolean;
  paying: boolean;
  onBack: () => void;
  bill: Bill | null;
  onClose: () => void;
}) {
  const [showCash, setShowCash] = useState(false);
  const [copied, setCopied] = useState(false);

  // Записаны — подтянулось поллингом после оплаты (или клиент уже был записан).
  // Проверяется раньше режима оплаты: как только вебхук завершил — сразу успех.
  if (offer.enrolled) {
    return (
      <Stack gap={2} alignItems="center" textAlign="center" py={2}>
        <CheckCircle sx={{ fontSize: 56, color: "#D9347E" }} />
        <Typography level="h4">
          <FormattedMessage defaultMessage="Вы записаны!" />
        </Typography>
        <Typography level="body-md" textColor="neutral.500">
          <FormattedMessage defaultMessage="Ждём вас 💜 Все детали пришлём в Telegram." />
        </Typography>
        <Button fullWidth onClick={onClose} sx={{ mt: 1 }}>
          <FormattedMessage defaultMessage="Хорошо" />
        </Button>
      </Stack>
    );
  }

  if (offer.placesLeft <= 0) {
    return (
      <Stack gap={2} textAlign="center" py={2}>
        <Typography level="h4">
          <FormattedMessage defaultMessage="Мест больше нет" />
        </Typography>
        <Typography level="body-md" textColor="neutral.500">
          <FormattedMessage defaultMessage="Все места разобрали. Напишите администратору — подскажем ближайшие даты." />
        </Typography>
        <Button variant="outlined" onClick={onClose}>
          <FormattedMessage defaultMessage="Закрыть" />
        </Button>
      </Stack>
    );
  }

  const bonusAllowed = offer.bonusRatio > 0 && offer.bonusAvailable > 0;
  const bonusApplied = bonusAllowed && useBonus ? offer.bonusAvailable : 0;
  // Курс всегда оплачивается полной ценой (баланс не учитываем); бонусы, если тип
  // их разрешает, идут скидкой к сумме.
  const toPay = Math.max(0, offer.price - bonusApplied);
  const lowPlaces = offer.placesLeft <= 3;

  // Шаг оплаты через ЕРИП: реквизиты прямо здесь + живое ожидание записи.
  if (paying) {
    const onCopy = async () => {
      if (!bill) return;
      await navigator.clipboard.writeText(bill.fullAccountNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <Stack gap={2.5} py={1}>
        <Box>
          <Button variant="plain" color="neutral" size="sm" onClick={onBack} sx={{ ml: -1 }}>
            <FormattedMessage defaultMessage="← Назад" />
          </Button>
          <Typography level="h4">
            <FormattedMessage defaultMessage="Оплата через ЕРИП" />
          </Typography>
        </Box>

        <Alert color="primary" variant="soft">
          <Stack gap={0.5}>
            <Typography level="title-md">
              <FormattedMessage
                defaultMessage="Оплатите {value}"
                values={{ value: money(toPay, offer.currency) }}
              />
            </Typography>
            <Typography level="body-sm">
              <FormattedMessage defaultMessage="Как только оплата дойдёт, мы запишем вас автоматически — это окно само обновится." />
            </Typography>
          </Stack>
        </Alert>

        {!bill ? (
          <Stack alignItems="center" py={3}>
            <CircularProgress />
          </Stack>
        ) : (
          <>
            <Card variant="soft">
              <Stack gap={1}>
                <Typography level="body-xs" textColor="neutral.500">
                  <FormattedMessage defaultMessage="Номер счёта для оплаты" />
                </Typography>
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                  <Typography level="h3" sx={{ wordBreak: "break-all" }}>
                    {bill.fullAccountNumber}
                  </Typography>
                  <Button
                    size="sm"
                    variant="outlined"
                    color={copied ? "success" : "neutral"}
                    onClick={onCopy}
                    startDecorator={copied ? <Done /> : <ContentCopy />}
                  >
                    {copied ? (
                      <FormattedMessage defaultMessage="Скопировано" />
                    ) : (
                      <FormattedMessage defaultMessage="Копировать" />
                    )}
                  </Button>
                </Stack>
              </Stack>
            </Card>

            {bill.qrCode && (
              <Stack alignItems="center" gap={1}>
                <Typography level="body-sm" textColor="neutral.500">
                  <FormattedMessage defaultMessage="Или отсканируйте QR-код в приложении банка" />
                </Typography>
                <Box
                  component="img"
                  src={bill.qrCode}
                  alt="QR"
                  sx={{ width: 200, height: 200, borderRadius: "sm", bgcolor: "#fff", p: 1 }}
                />
              </Stack>
            )}

            {bill.banks.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {bill.banks.map((bank) => (
                  <BankLinkView key={bank.name} bank={bank} />
                ))}
              </Box>
            )}
          </>
        )}

        <Divider />

        <Stack direction="row" alignItems="center" gap={1.5} sx={{ px: 0.5 }}>
          <CircularProgress size="sm" />
          <Typography level="body-sm" textColor="neutral.500">
            <FormattedMessage defaultMessage="Ждём оплату — запишем автоматически, обычно за пару минут. Можно закрыть приложение." />
          </Typography>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack gap={2.5} py={1}>
      <Stack gap={0.5}>
        <Typography level="h4">{offer.title}</Typography>
        <Typography level="body-sm" textColor={lowPlaces ? "#D9347E" : "neutral.500"}>
          <FormattedMessage
            defaultMessage="Осталось {count, plural, one {# место} few {# места} other {# мест}}"
            values={{ count: offer.placesLeft }}
          />
        </Typography>
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="baseline">
        <Typography level="body-md" textColor="neutral.500">
          <FormattedMessage defaultMessage="Стоимость" />
        </Typography>
        <Typography level="title-lg">{money(offer.price, offer.currency)}</Typography>
      </Stack>

      {bonusAllowed && (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 1.5, borderRadius: "sm", bgcolor: "background.level1" }}
        >
          <Box>
            <Typography level="body-sm">
              <FormattedMessage defaultMessage="Оплатить бонусами" />
            </Typography>
            <Typography level="body-xs" textColor="neutral.500">
              <FormattedMessage
                defaultMessage="Спишется {value}"
                values={{ value: money(offer.bonusAvailable, offer.currency) }}
              />
            </Typography>
          </Box>
          <Switch checked={useBonus} onChange={(e) => onToggleBonus(e.target.checked)} />
        </Stack>
      )}

      {offer.pendingEnrollment && (
        <Alert color="success" variant="soft" size="sm">
          <FormattedMessage defaultMessage="Заявка принята — запишем вас, как только дойдёт оплата." />
        </Alert>
      )}

      <Divider />

      <Stack gap={1.5}>
        <Button size="lg" loading={requesting} onClick={onPayOnline}>
          <FormattedMessage defaultMessage="Оплатить онлайн" />
        </Button>
        <Typography level="body-xs" textColor="neutral.500" textAlign="center">
          <FormattedMessage defaultMessage="Оплата через ЕРИП в приложении банка. Как только деньги дойдут — запишем вас автоматически." />
        </Typography>

        <Button variant="plain" color="neutral" onClick={() => setShowCash((v) => !v)}>
          <FormattedMessage defaultMessage="Оплатить наличными на филиале" />
        </Button>

        {showCash && (
          <Stack gap={1} sx={{ p: 1.5, borderRadius: "sm", bgcolor: "background.level1" }}>
            <Typography level="body-sm">
              <FormattedMessage defaultMessage="Оплатить можно у администратора на любом из филиалов:" />
            </Typography>
            {CASH_BRANCHES.map((branch) => (
              <Typography key={branch.name} level="body-sm" textColor="neutral.600">
                <b>{branch.name}</b> — {branch.address}
              </Typography>
            ))}
            <Typography level="body-xs" textColor="#D9347E" sx={{ mt: 0.5 }}>
              <FormattedMessage defaultMessage="Место закрепляется только после оплаты — лучше не откладывать, чтобы его не заняла другая ученица 💜" />
            </Typography>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
